import MessageDraftAPI from 'dashboard/api/messageDraft';
import types from '../mutation-types';

let uploadIdCounter = 0;

const uploadQueues = {};

const getQueue = conversationId => {
  if (!uploadQueues[conversationId]) {
    uploadQueues[conversationId] = {
      items: [],
      processing: false,
      activeAbort: null,
      activeTempId: null,
    };
  }
  return uploadQueues[conversationId];
};

const state = {
  records: {},
  uploadingFiles: {},
  uiFlags: {
    isFetching: false,
    isUpdating: false,
    isUploadingFile: false,
  },
};

export const getters = {
  getDraft: _state => conversationId => {
    return _state.records[conversationId] || null;
  },
  getUploadingFiles: _state => conversationId => {
    return _state.uploadingFiles[conversationId] || [];
  },
  getUIFlags: _state => _state.uiFlags,
};

let updateTimers = {};

export const actions = {
  fetch: async ({ commit }, conversationId) => {
    if (!conversationId) return;
    commit(types.SET_MESSAGE_DRAFT_UI_FLAG, { isFetching: true });
    try {
      const { data } = await MessageDraftAPI.show(conversationId);
      commit(types.SET_MESSAGE_DRAFT, {
        conversationId,
        draft: {
          id: data.id,
          publicContent: data.public_content,
          privateContent: data.private_content,
          publicFiles: data.public_files || [],
          privateFiles: data.private_files || [],
          ccEmails: data.cc_emails || [],
          bccEmails: data.bcc_emails || [],
          toEmails: data.to_emails || [],
          includeOriginalMessage: data.include_original_message ?? false,
          emailSubject: data.email_subject || '',
          hasPublicContent: data.has_public_content || false,
          hasPrivateContent: data.has_private_content || false,
        },
      });
    } finally {
      commit(types.SET_MESSAGE_DRAFT_UI_FLAG, { isFetching: false });
    }
  },

  update: (
    { commit, state: _state },
    {
      conversationId,
      publicContent,
      privateContent,
      ccEmails,
      bccEmails,
      toEmails,
      includeOriginalMessage,
      emailSubject,
      immediate = false,
    }
  ) => {
    const existing = _state.records[conversationId] || {
      id: null,
      publicContent: null,
      privateContent: null,
      publicFiles: [],
      privateFiles: [],
      ccEmails: [],
      bccEmails: [],
      toEmails: [],
      includeOriginalMessage: false,
      emailSubject: '',
    };
    const updated = { ...existing };
    if (publicContent !== undefined) updated.publicContent = publicContent;
    if (privateContent !== undefined) updated.privateContent = privateContent;
    if (ccEmails !== undefined) updated.ccEmails = ccEmails;
    if (bccEmails !== undefined) updated.bccEmails = bccEmails;
    if (toEmails !== undefined) updated.toEmails = toEmails;
    if (includeOriginalMessage !== undefined)
      updated.includeOriginalMessage = includeOriginalMessage;
    if (emailSubject !== undefined) updated.emailSubject = emailSubject;
    commit(types.SET_MESSAGE_DRAFT, { conversationId, draft: updated });

    const saveToBackend = async () => {
      commit(types.SET_MESSAGE_DRAFT_UI_FLAG, { isUpdating: true });
      const payload = {};
      if (publicContent !== undefined) payload.publicContent = publicContent;
      if (privateContent !== undefined) payload.privateContent = privateContent;
      if (ccEmails !== undefined) payload.ccEmails = ccEmails;
      if (bccEmails !== undefined) payload.bccEmails = bccEmails;
      if (toEmails !== undefined) payload.toEmails = toEmails;
      if (includeOriginalMessage !== undefined)
        payload.includeOriginalMessage = includeOriginalMessage;
      if (emailSubject !== undefined) payload.emailSubject = emailSubject;
      const { data } = await MessageDraftAPI.update(conversationId, payload);
      if (data.id) {
        const current = _state.records[conversationId] || {};
        commit(types.SET_MESSAGE_DRAFT, {
          conversationId,
          draft: {
            ...current,
            id: data.id,
          },
        });
      }
      commit(types.SET_MESSAGE_DRAFT_UI_FLAG, { isUpdating: false });
    };

    if (updateTimers[conversationId]) {
      clearTimeout(updateTimers[conversationId]);
      delete updateTimers[conversationId];
    }

    if (immediate) {
      saveToBackend();
      return;
    }
    updateTimers[conversationId] = setTimeout(saveToBackend, 500);
  },

  addAttachment: (
    { commit, dispatch },
    { conversationId, file, fileMeta, isPrivate }
  ) => {
    uploadIdCounter += 1;
    const tempId = `uploading-${uploadIdCounter}`;
    const queue = getQueue(conversationId);

    queue.items.push({ tempId, file, fileMeta, isPrivate });

    commit(types.ADD_MESSAGE_DRAFT_UPLOADING_FILE, {
      conversationId,
      file: {
        tempId,
        filename: fileMeta?.name || 'file',
        byte_size: fileMeta?.size || 0,
        content_type: fileMeta?.type || 'application/octet-stream',
        status: 'queued',
        isPrivate,
      },
    });

    dispatch('processUploadQueue', conversationId);
  },

  processUploadQueue: async ({ commit, state: _state }, conversationId) => {
    const queue = getQueue(conversationId);
    if (queue.processing) return;
    queue.processing = true;

    while (queue.items.length > 0) {
      const item = queue.items.shift();
      const abortController = new AbortController();
      queue.activeAbort = abortController;
      queue.activeTempId = item.tempId;

      commit(types.SET_MESSAGE_DRAFT_UPLOADING_FILE_STATUS, {
        conversationId,
        tempId: item.tempId,
        status: 'uploading',
      });

      commit(types.SET_MESSAGE_DRAFT_UI_FLAG, { isUploadingFile: true });
      const apiCall =
        typeof item.file === 'string'
          ? MessageDraftAPI.addAttachmentBlob
          : MessageDraftAPI.addAttachmentFile;
      // eslint-disable-next-line no-await-in-loop
      const { data } = await apiCall.call(
        MessageDraftAPI,
        conversationId,
        item.file,
        { signal: abortController.signal, isPrivate: item.isPrivate }
      );
      const existing = _state.records[conversationId] || {
        id: null,
        publicContent: null,
        privateContent: null,
        publicFiles: [],
        privateFiles: [],
      };
      commit(types.SET_MESSAGE_DRAFT, {
        conversationId,
        draft: {
          ...existing,
          publicFiles: data.public_files || [],
          privateFiles: data.private_files || [],
        },
      });
      commit(types.REMOVE_MESSAGE_DRAFT_UPLOADING_FILE, {
        conversationId,
        tempId: item.tempId,
      });
      queue.activeAbort = null;
      queue.activeTempId = null;
    }

    queue.processing = false;
    commit(types.SET_MESSAGE_DRAFT_UI_FLAG, { isUploadingFile: false });
  },

  cancelUpload: ({ commit }, { conversationId, tempId }) => {
    const queue = getQueue(conversationId);

    if (queue.activeTempId === tempId && queue.activeAbort) {
      queue.activeAbort.abort();
    } else {
      queue.items = queue.items.filter(item => item.tempId !== tempId);
    }

    commit(types.REMOVE_MESSAGE_DRAFT_UPLOADING_FILE, {
      conversationId,
      tempId,
    });
  },

  removeAttachment: async ({ commit }, { conversationId, attachmentId }) => {
    commit(types.REMOVE_MESSAGE_DRAFT_FILE, { conversationId, attachmentId });
    await MessageDraftAPI.removeAttachment(conversationId, attachmentId);
  },

  clearPublicAndReload: async ({ commit, dispatch }, conversationId) => {
    commit(types.CLEAR_MESSAGE_DRAFT_PUBLIC, { conversationId });
    await dispatch('fetch', conversationId);
  },

  destroy: async ({ commit }, conversationId) => {
    await MessageDraftAPI.destroy(conversationId);
    commit(types.CLEAR_MESSAGE_DRAFT, { conversationId });
  },
};

export const mutations = {
  [types.SET_MESSAGE_DRAFT]($state, { conversationId, draft }) {
    $state.records = {
      ...$state.records,
      [conversationId]: draft,
    };
  },
  [types.SET_MESSAGE_DRAFT_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.CLEAR_MESSAGE_DRAFT]($state, { conversationId }) {
    const { [conversationId]: _, ...rest } = $state.records;
    $state.records = rest;
  },
  [types.CLEAR_MESSAGE_DRAFT_PUBLIC]($state, { conversationId }) {
    const existing = $state.records[conversationId];
    if (existing) {
      $state.records = {
        ...$state.records,
        [conversationId]: {
          ...existing,
          publicContent: null,
          publicFiles: [],
          ccEmails: [],
          bccEmails: [],
          toEmails: [],
          includeOriginalMessage: true,
          emailSubject: '',
        },
      };
    }
  },
  [types.CLEAR_MESSAGE_DRAFT_PRIVATE]($state, { conversationId }) {
    const existing = $state.records[conversationId];
    if (existing) {
      $state.records = {
        ...$state.records,
        [conversationId]: {
          ...existing,
          privateContent: null,
          privateFiles: [],
        },
      };
    }
  },
  [types.ADD_MESSAGE_DRAFT_FILE]($state, { conversationId, file }) {
    const existing = $state.records[conversationId];
    if (existing) {
      $state.records = {
        ...$state.records,
        [conversationId]: {
          ...existing,
          publicFiles: [...existing.publicFiles, file],
        },
      };
    }
  },
  [types.REMOVE_MESSAGE_DRAFT_FILE]($state, { conversationId, attachmentId }) {
    const existing = $state.records[conversationId];
    if (existing) {
      $state.records = {
        ...$state.records,
        [conversationId]: {
          ...existing,
          publicFiles: existing.publicFiles.filter(f => f.id !== attachmentId),
          privateFiles: existing.privateFiles.filter(
            f => f.id !== attachmentId
          ),
        },
      };
    }
  },
  [types.ADD_MESSAGE_DRAFT_UPLOADING_FILE]($state, { conversationId, file }) {
    $state.uploadingFiles = {
      ...$state.uploadingFiles,
      [conversationId]: [
        ...($state.uploadingFiles[conversationId] || []),
        file,
      ],
    };
  },
  [types.REMOVE_MESSAGE_DRAFT_UPLOADING_FILE](
    $state,
    { conversationId, tempId }
  ) {
    const existing = $state.uploadingFiles[conversationId] || [];
    const filtered = existing.filter(f => f.tempId !== tempId);
    $state.uploadingFiles = {
      ...$state.uploadingFiles,
      [conversationId]: filtered,
    };
  },
  [types.SET_MESSAGE_DRAFT_UPLOADING_FILE_STATUS](
    $state,
    { conversationId, tempId, status }
  ) {
    const existing = $state.uploadingFiles[conversationId] || [];
    $state.uploadingFiles = {
      ...$state.uploadingFiles,
      [conversationId]: existing.map(f =>
        f.tempId === tempId ? { ...f, status } : f
      ),
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
